import React, { useEffect, useRef, useState } from 'react';

/**
 * #163 follow-up: confirm undoing an approved check-in (back to pending).
 * Optional reason, recorded in the CHECKIN_REVERSED audit entry. Focus starts
 * on the safe choice; Escape cancels.
 */
interface UndoCheckInDialogProps {
  registration: { id: string; participantName?: string | null } | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

const UndoCheckInDialog: React.FC<UndoCheckInDialogProps> = ({ registration, busy, onCancel, onConfirm }) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (registration) { setReason(''); cancelRef.current?.focus(); }
  }, [registration]);
  if (!registration) return null;
  const name = registration.participantName || 'this person';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="undo-checkin-title"
        aria-describedby="undo-checkin-desc"
        onKeyDown={(e) => { if (e.key === 'Escape' && !busy) onCancel(); }}
        className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl"
      >
        <h2 id="undo-checkin-title" className="text-lg font-bold text-gray-900">Undo check-in?</h2>
        <p id="undo-checkin-desc" className="mt-2 text-sm text-gray-700">
          Undo check-in for <strong>{name}</strong>? They go back to pending. Their payment is not changed.
        </p>
        <label htmlFor="undo-checkin-reason" className="mt-3 block text-sm font-medium text-gray-700">
          Reason (optional)
        </label>
        <input
          id="undo-checkin-reason"
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="mt-1 w-full min-h-[44px] rounded-md border border-gray-300 px-3 py-2 text-sm"
          placeholder="For example: ticked the wrong person"
        />
        <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
          >
            Keep check-in
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason.trim())}
            disabled={busy}
            className="min-h-[44px] rounded-lg bg-fishing-blue px-4 py-2 font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
          >
            {busy ? 'Undoing…' : 'Undo check-in'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UndoCheckInDialog;
